<template>
  <div class="admin-questionnaires-wrap">
    <main class="page">

      <p class="breadcrumb">SEQ / Admin / Questionnaires</p>

      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Questionnaires</h1>
          <p class="page-subtitle">Manage the SEQ questionnaires for your organization.</p>
        </div>
        <div class="header-actions">
          <button
            type="button"
            class="btn btn-secondary import-button"
            title="Import a questionnaire from a JSON payload (experimental, dev only)"
            @click="openImport"
          >Import JSON</button>
          <button
            type="button"
            class="btn btn-primary new-button"
            @click="openCreate"
          >
            <svg class="plus-icon" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            New questionnaire
          </button>
        </div>
      </div>

      <div class="meta-strip">
        <div class="meta-item">
          <span class="meta-label">Organization</span>
          <span class="meta-value">{{ organizationName }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Total</span>
          <span class="meta-value">{{ questionnaires.length }}</span>
        </div>
      </div>

      <section class="section">
        <h2 class="section-title">All questionnaires</h2>

        <p
          v-if="loadError"
          class="error-banner"
        >{{ loadError }}</p>

        <div
          v-if="loading"
          class="loading-state"
          role="status"
          aria-live="polite"
        >
          <span class="spinner" aria-hidden="true"></span>
          <p class="loading-text">Loading questionnaires<span class="loading-dots"><i>.</i><i>.</i><i>.</i></span></p>
        </div>

        <p
          v-else-if="questionnaires.length === 0"
          class="empty-state"
        >
          No questionnaires yet. Create your first one to get started.
        </p>

        <div v-else class="questionnaire-list">
          <button
            v-for="q in questionnaires"
            :key="q.id"
            type="button"
            class="questionnaire-row"
            @click="openQuestionnaire(q)"
          >
            <div class="row-main">
              <div class="row-name">
                <span>{{ q.name }}</span>
                <span
                  v-if="q.is_locked"
                  class="lock-icon"
                  title="Gesperrt – kann nicht mehr bearbeitet werden"
                  aria-label="locked"
                >🔒</span>
              </div>
              <div class="row-slug">{{ q.slug }}</div>
            </div>
            <div class="row-meta">
              <span class="status-pill" :class="`status-${q.status}`">{{ q.status }}</span>
              <span class="row-date">{{ formatDate(q.created_at) }}</span>
              <span class="row-chevron" aria-hidden="true">›</span>
            </div>
          </button>
        </div>
      </section>

    </main>

    <QuestionnaireFormModal
      v-if="showCreate"
      mode="create"
      :server-error="createError"
      @submit="onCreateSubmit"
      @cancel="onCreateCancel"
    />

    <ImportQuestionnaireModal
      v-if="showImport"
      @close="showImport = false"
      @imported="onImported"
    />
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import { questionnairesState } from './_data/questionnaires.js';
import QuestionnaireFormModal from './QuestionnaireFormModal.vue';
import ImportQuestionnaireModal from './ImportQuestionnaireModal.vue';

const dateFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export default {
  name: 'AdminQuestionnaireList',
  components: { QuestionnaireFormModal, ImportQuestionnaireModal },
  data() {
    return {
      organizationName: 'Berner Fachhochschule',
      // Toggle to true to test the empty state.
      isEmpty: false,
      showCreate: false,
      showImport: false,
      createError: null,
      loading: true,
      loadError: null,
    };
  },
  computed: {
    questionnaires() {
      return this.isEmpty ? [] : questionnairesState.items;
    },
  },
  async created() {
    // GET /api/seq/questionnaires — backend scopes to caller's organization.
    // On success, replace the shared store's items with the live response.
    // On failure, keep whatever is already in the store (currently the
    // dummy seed) so the UI still renders and the developer can iterate
    // without a backend running.
    try {
      const items = await apiService.fetchQuestionnaires();
      questionnairesState.items = items;
      this.loadError = null;
    } catch (e) {
      console.error('Failed to fetch questionnaires from backend:', e);
      this.loadError = 'Could not load questionnaires from the server. Showing local data.';
    } finally {
      this.loading = false;
    }

    // Edit and Delete still hit only the local store (see Detail page);
    // their backend endpoints land in subsequent iterations:
    //
    //   PATCH  /api/seq/questionnaires/:id       → apiService.updateQuestionnaire(id, patch)
    //   DELETE /api/seq/questionnaires/:id       → apiService.deleteQuestionnaire(id)
  },
  methods: {
    openQuestionnaire(q) {
      this.$router.push(`/admin/questionnaires/${q.slug}`);
    },
    openCreate() {
      this.createError = null;
      this.showCreate = true;
    },
    openImport() {
      this.showImport = true;
    },
    async onImported({ slug }) {
      // Refresh the list so the new questionnaire shows, then jump to it.
      this.showImport = false;
      try {
        questionnairesState.items = await apiService.fetchQuestionnaires();
      } catch (e) {
        // Non-fatal — the import succeeded; the list just won't refresh.
      }
      if (slug) this.$router.push(`/admin/questionnaires/${slug}`);
    },
    onCreateCancel() {
      this.showCreate = false;
      this.createError = null;
    },
    async onCreateSubmit(payload) {
      // POST /api/seq/questionnaires. On success the backend returns the
      // full row including the server-assigned id, is_locked, and
      // timestamps — we prepend it to the store so it shows at the top of
      // the list (the GET sorts by created_at DESC).
      this.createError = null;
      try {
        const created = await apiService.createQuestionnaire(payload);
        questionnairesState.items.unshift(created);
        this.showCreate = false;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (Array.isArray(detail)) {
          // FastAPI 422 from Pydantic validation returns an array of issues.
          this.createError = detail.map(d => d.msg).join(' ');
        } else if (typeof detail === 'string') {
          this.createError = detail;
        } else {
          this.createError = 'Failed to create questionnaire. Please try again.';
        }
        // Modal stays open with the error banner so the user can correct
        // and retry without losing their input.
      }
    },
    formatDate(iso) {
      if (!iso) return '';
      return dateFmt.format(new Date(iso));
    },
  },
};
</script>

<style scoped>
.admin-questionnaires-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.admin-questionnaires-wrap *,
.admin-questionnaires-wrap *::before,
.admin-questionnaires-wrap *::after {
  box-sizing: border-box;
}

.page {
  max-width: 820px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
}

.breadcrumb {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.page-header-text {
  flex: 1;
  min-width: 240px;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.5rem;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 17px;
  color: var(--color-text-secondary);
  margin: 0;
  font-weight: 400;
}

.btn {
  padding: 10px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s;
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}

.btn-secondary {
  background: white;
  color: var(--color-text-primary);
}

.btn-secondary:hover {
  background: var(--color-bg-page, #f5f4ef);
  border-color: rgba(0, 0, 0, 0.2);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-top: 4px;
}

.new-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  white-space: nowrap;
}

.plus-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.meta-strip {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 14px 18px;
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
  margin-bottom: 2.5rem;
  font-size: 13px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-label {
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
  font-weight: 500;
}

.meta-value {
  color: var(--color-text-primary);
  font-weight: 500;
}

.section {
  margin-bottom: 2.5rem;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 1rem;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--color-accent);
  display: inline-block;
}

.empty-state {
  font-size: 14px;
  color: var(--color-text-tertiary);
  background: var(--color-bg-soft);
  border: 0.5px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: 32px 20px;
  text-align: center;
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 32px 0;
  font-size: 13px;
  color: var(--color-text-tertiary);
}

.loading-text {
  margin: 0;
}

/* Spinning ring in the accent colour — mirrors the quiz runtime spinner. */
.spinner {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid var(--color-accent-soft);
  border-top-color: var(--color-accent);
  animation: spinner-rotate 0.8s linear infinite;
}

@keyframes spinner-rotate {
  to { transform: rotate(360deg); }
}

/* Animated trailing dots after the label. */
.loading-dots i {
  font-style: normal;
  opacity: 0;
  animation: loading-dot 1.4s infinite both;
}
.loading-dots i:nth-child(2) { animation-delay: 0.2s; }
.loading-dots i:nth-child(3) { animation-delay: 0.4s; }

@keyframes loading-dot {
  0%   { opacity: 0; }
  40%  { opacity: 1; }
  100% { opacity: 0; }
}

/* Respect reduced-motion: drop the animation, keep a static indicator. */
@media (prefers-reduced-motion: reduce) {
  .spinner {
    animation: none;
    border-top-color: var(--color-accent-soft);
  }
  .loading-dots i {
    animation: none;
    opacity: 1;
  }
}

.error-banner {
  font-size: 13px;
  color: #6B4A0F;
  background: rgba(232, 163, 61, 0.18);
  border: 0.5px solid rgba(232, 163, 61, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 12px;
}

.questionnaire-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.questionnaire-row {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  background: var(--color-bg-card);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 14px 18px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;
}

.questionnaire-row:hover {
  border-color: var(--color-accent);
  background: var(--color-bg-soft);
}

.questionnaire-row:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.row-main {
  flex: 1;
  min-width: 0;
}

.row-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.lock-icon {
  font-size: 13px;
  line-height: 1;
}

.row-slug {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-text-tertiary);
  letter-spacing: 0.01em;
}

.row-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.status-pill {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 3px 9px;
  border-radius: 4px;
  white-space: nowrap;
}

.status-pill.status-draft {
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
}

.status-pill.status-active {
  background: var(--color-accent-soft);
  color: var(--color-accent-dark);
}

.status-pill.status-archived {
  background: var(--color-bg-soft);
  color: var(--color-text-tertiary);
}

.row-date {
  font-size: 12px;
  color: var(--color-text-tertiary);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  min-width: 96px;
  text-align: right;
}

.row-chevron {
  color: var(--color-text-tertiary);
  font-size: 18px;
  line-height: 1;
}

.questionnaire-row:hover .row-chevron {
  color: var(--color-accent);
}

@media (max-width: 600px) {
  .questionnaire-row {
    flex-wrap: wrap;
  }
  .row-meta {
    width: 100%;
    justify-content: flex-start;
    margin-top: 6px;
  }
  .row-date {
    flex: 1;
    text-align: left;
    min-width: 0;
  }
}
</style>
